/**
 * v1.5 阶段 5-B：admin RBAC 管理页（内嵌 ControlPanel "🔐 权限管理" tab）
 *
 * 3 子视图（顶部子 tab 切换）：
 *   ① 角色权限矩阵 — 左角色列表 / 右 module 分组权限矩阵 / 全量 PUT 保存
 *   ② 用户级覆盖   — 搜用户 → 看 effective + grants/revokes / 加减 / 过期
 *   ③ 审计日志     — 过滤 + 分页
 *
 * 全部 API @RequirePermissions('system:rbac_manage')，仅 admin 可见。
 * 入口由 ControlPanel 的 items.show=useHasPermission(...) 控制。
 *
 * ⚠️ 双保险防自锁：
 *   - admin 角色的 system:rbac_manage 勾选框 disabled
 *   - 给 admin 自己加 revoke system:rbac_manage 前端拦
 */
import { useMemo, useState } from 'react';
import { Input, Modal, message, Popconfirm, DatePicker, Select } from 'antd';
import dayjs from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { $ } from '../../lib/api';
import { Btn, Pill } from '../../components/ui';
import { fmtDateTime, fmtMonthDayTime } from '../../lib/format';
import { useAuthStore } from '../../store/auth';

const ADMIN_LOCKED = new Set(['system:rbac_manage']);

// 大权限批 P3：orphan 权限点——全仓后端 @RequirePermissions / 前端 gate 零引用，勾选对系统行为无影响。
//   在矩阵里【置灰 + 标"未接入"】，避免 admin 误授空壳权限点(bug9 即误授死点 user:manage_permissions 所致)。
//   勿删定义(is_system + seed 重跑会复活)；这里只做 UI 过滤标注。来源:大权限批 D4 全量交叉比对。
const ORPHAN_PERMS = new Set([
  'approval:view_all', 'customer:import', 'dashboard:finance_workbench', 'im:archive_view', 'im:send',
  'import:view_history', 'investment:cost_import', 'invoice:ship', 'lead:reassign', 'material:edit',
  'order:approve', 'order:edit', 'order:refund', 'payment:edit', 'payment:reconcile', 'report:financial_view',
  'system:control_panel', 'system:lifecycle_config', 'team:view_all', 'user:manage_permissions',
]);

type Permission = {
  id: number; code: string; name: string; module: string;
  action: string; description: string | null; is_system: number;
};
type Role = {
  id: number; code: string; name: string; description: string | null;
  data_scope: string; is_system: number; perm_count: number; user_count: number;
};
type RoleDetail = { role: { id: number; code: string; name: string; is_system: number }; perm_codes: string[] };
type Effective = {
  user: { id: number; real_name: string; role: string; role_code: string | null; role_name: string | null };
  role_perms: string[];
  grants: Array<{ code: string; expires_at: string | null; reason: string | null }>;
  revokes: Array<{ code: string; reason: string | null }>;
  effective: string[];
};

export function RbacAdmin() {
  const [sub, setSub] = useState<'role' | 'user' | 'audit'>('role');
  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, borderBottom: '1px solid #DEE0E3' }}>
        {[
          { k: 'role', l: '🛡️ 角色权限矩阵' },
          { k: 'user', l: '👤 用户级覆盖' },
          { k: 'audit', l: '📜 审计日志' },
        ].map((t) => (
          <div key={t.k}
               onClick={() => setSub(t.k as any)}
               style={{
                 padding: '10px 16px', cursor: 'pointer', fontSize: 14,
                 borderBottom: sub === t.k ? '2px solid #2C5BFF' : '2px solid transparent',
                 color: sub === t.k ? '#2C5BFF' : '#1F2329',
                 fontWeight: sub === t.k ? 500 : 400, marginBottom: -1,
               }}>{t.l}</div>
        ))}
      </div>
      {sub === 'role' && <RoleMatrix />}
      {sub === 'user' && <UserOverride />}
      {sub === 'audit' && <AuditLogs />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * ① 角色权限矩阵
 * ═══════════════════════════════════════════════════════════════ */
function RoleMatrix() {
  const qc = useQueryClient();
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [draft, setDraft] = useState<Set<string>>(new Set());
  const [confirming, setConfirming] = useState(false);

  const { data: perms = [] } = useQuery<Permission[]>({
    queryKey: ['rbac-permissions'],
    queryFn: () => $.get('/admin/rbac/permissions'),
    staleTime: 60_000,
  });
  const { data: roles = [] } = useQuery<Role[]>({
    queryKey: ['rbac-roles'],
    queryFn: () => $.get('/admin/rbac/roles'),
    staleTime: 30_000,
  });
  const { data: roleDetail } = useQuery<RoleDetail>({
    queryKey: ['rbac-role-detail', selectedRoleId],
    queryFn: () => $.get(`/admin/rbac/roles/${selectedRoleId}/permissions`),
    enabled: selectedRoleId != null,
  });

  // 选中角色变化时同步 draft
  useMemo(() => {
    if (roleDetail) setDraft(new Set(roleDetail.perm_codes));
  }, [roleDetail?.role?.id]);

  const selectedRole = roles.find((r) => r.id === selectedRoleId);
  const isAdminRole = selectedRole?.code === 'admin';

  // module 分组
  const grouped = useMemo(() => {
    const m = new Map<string, Permission[]>();
    perms.forEach((p) => {
      if (!m.has(p.module)) m.set(p.module, []);
      m.get(p.module)!.push(p);
    });
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [perms]);

  const toggle = (code: string) => {
    if (isAdminRole && ADMIN_LOCKED.has(code)) {
      message.warning(`admin 角色不能去掉 ${code}（防自锁）`);
      return;
    }
    setDraft((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  };

  const orig = roleDetail?.perm_codes ?? [];
  const origSet = new Set(orig);
  const added = [...draft].filter((c) => !origSet.has(c));
  const removed = orig.filter((c) => !draft.has(c));
  const dirty = added.length > 0 || removed.length > 0;

  const save = useMutation({
    mutationFn: () => $.put(`/admin/rbac/roles/${selectedRoleId}/permissions`, { perm_codes: [...draft] }),
    onSuccess: (data: any) => {
      message.success(`已保存 · 加 ${data?.added?.length ?? 0} 删 ${data?.removed?.length ?? 0} · 失效 ${data?.cache_invalidated_users ?? 0} 个用户缓存`);
      setConfirming(false);
      qc.invalidateQueries({ queryKey: ['rbac-roles'] });
      qc.invalidateQueries({ queryKey: ['rbac-role-detail'] });
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message ?? e?.message ?? '保存失败';
      message.error(msg);
      setConfirming(false);
    },
  });

  return (
    <div style={{ display: 'flex', gap: 16 }}>
      {/* 左：角色列表 */}
      <aside style={{ width: 220, flexShrink: 0 }}>
        <div style={{ padding: 12, background: '#FFFFFF', border: '1px solid #DEE0E3', borderRadius: 8 }}>
          <div style={{ fontSize: 13, color: '#646A73', marginBottom: 8 }}>选择角色</div>
          {roles.map((r) => (
            <div key={r.id}
                 onClick={() => setSelectedRoleId(r.id)}
                 style={{
                   padding: '10px 12px', marginBottom: 4, borderRadius: 6, cursor: 'pointer',
                   background: selectedRoleId === r.id ? '#EFF2FE' : 'transparent',
                   color: selectedRoleId === r.id ? '#2C5BFF' : '#1F2329',
                 }}>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{r.name}</div>
              <div style={{ fontSize: 12, color: '#8F959E', marginTop: 2 }}>
                {r.code} · {r.perm_count} 权限 · {r.user_count} 用户
                {r.is_system ? ' · 系统' : ''}
              </div>
            </div>
          ))}
        </div>
      </aside>

      {/* 右：矩阵 */}
      <main style={{ flex: 1, minWidth: 0 }}>
        {selectedRoleId == null ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#8F959E' }}>请从左侧选择一个角色</div>
        ) : (
          <>
            <div style={{
              padding: 16, background: '#FFFFFF', border: '1px solid #DEE0E3', borderRadius: 8,
              marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
              <div>
                <div style={{ fontSize: 16, fontWeight: 500 }}>
                  {selectedRole?.name} <span style={{ fontSize: 12, color: '#8F959E', marginLeft: 6 }}>{selectedRole?.code}</span>
                  {isAdminRole && <Pill tone="warning">⚠️ 系统角色 · 防自锁</Pill>}
                </div>
                <div style={{ fontSize: 12, color: '#646A73', marginTop: 4 }}>
                  当前 {orig.length} 个 · 草稿 {draft.size} 个
                  {dirty && (
                    <span style={{ marginLeft: 8, color: '#FF6B00' }}>
                      · 待加 <strong>{added.length}</strong> · 待删 <strong>{removed.length}</strong>
                    </span>
                  )}
                  {selectedRole && <span style={{ marginLeft: 8 }}>· 影响 <strong>{selectedRole.user_count}</strong> 个用户</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <Btn onClick={() => setDraft(new Set(orig))} disabled={!dirty}>放弃改动</Btn>
                <Btn kind="primary" onClick={() => setConfirming(true)} disabled={!dirty || save.isPending}>
                  保存…
                </Btn>
              </div>
            </div>

            {grouped.map(([mod, list]) => (
              <div key={mod} style={{
                marginBottom: 12, padding: 16, background: '#FFFFFF',
                border: '1px solid #DEE0E3', borderRadius: 8,
              }}>
                <div style={{
                  fontSize: 13, fontWeight: 500, color: '#1F2329', marginBottom: 10,
                  display: 'flex', alignItems: 'center', gap: 8,
                }}>
                  <span>{mod}</span>
                  <span style={{ fontSize: 11, color: '#8F959E', fontWeight: 400 }}>
                    {list.filter((p) => draft.has(p.code)).length}/{list.length}
                  </span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 8 }}>
                  {list.map((p) => {
                    const checked = draft.has(p.code);
                    const isOrphan = ORPHAN_PERMS.has(p.code); // 大权限批 P3：空壳权限点，置灰+标未接入、禁勾(授了无效)
                    const locked = (isAdminRole && ADMIN_LOCKED.has(p.code)) || isOrphan;
                    const wasOrig = origSet.has(p.code);
                    const bg = locked ? '#F5F6F7'
                      : checked && !wasOrig ? '#E8F7EE'
                      : !checked && wasOrig ? '#FEEBEC'
                      : checked ? '#EFF2FE' : '#FFFFFF';
                    return (
                      <label key={p.code} style={{
                        display: 'flex', gap: 8, padding: '8px 10px', borderRadius: 6,
                        border: '1px solid #DEE0E3', cursor: locked ? 'not-allowed' : 'pointer',
                        background: bg, opacity: locked ? 0.7 : 1,
                      }}>
                        <input type="checkbox" checked={checked} disabled={locked}
                               onChange={() => toggle(p.code)} style={{ marginTop: 3 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, color: '#1F2329' }}>
                            {p.name}
                            {p.is_system === 1 && <span style={{ marginLeft: 4, fontSize: 10, color: '#8F959E' }}>系统</span>}
                            {isOrphan && <span style={{ marginLeft: 4, fontSize: 10, color: '#FF6B00' }}>未接入</span>}
                          </div>
                          <div style={{ fontSize: 11, color: '#8F959E', fontFamily: 'monospace' }}>{p.code}</div>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
          </>
        )}

        <Modal open={confirming} title="确认改角色权限"
               onCancel={() => setConfirming(false)}
               onOk={() => save.mutate()}
               okText={`确认保存（影响 ${selectedRole?.user_count ?? 0} 用户）`}
               cancelText="取消" confirmLoading={save.isPending}>
          <p>角色：<strong>{selectedRole?.name}</strong>（{selectedRole?.code}）</p>
          <p>影响用户数：<strong style={{ color: '#FF6B00' }}>{selectedRole?.user_count ?? 0}</strong>（保存后立即对这些用户生效，缓存自动失效）</p>
          {added.length > 0 && (
            <p style={{ color: '#1A7F37' }}>新增 {added.length} 个权限：
              <div style={{ fontSize: 12, color: '#646A73', marginTop: 4, fontFamily: 'monospace' }}>{added.join(', ')}</div>
            </p>
          )}
          {removed.length > 0 && (
            <p style={{ color: '#CF222E' }}>移除 {removed.length} 个权限：
              <div style={{ fontSize: 12, color: '#646A73', marginTop: 4, fontFamily: 'monospace' }}>{removed.join(', ')}</div>
            </p>
          )}
        </Modal>
      </main>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * ② 用户级覆盖
 * ═══════════════════════════════════════════════════════════════ */
function UserOverride() {
  const qc = useQueryClient();
  const meUser = useAuthStore((s) => s.user);
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [addingType, setAddingType] = useState<'grant' | 'revoke' | null>(null);
  const [addPermCode, setAddPermCode] = useState('');
  const [addReason, setAddReason] = useState('');
  const [addExpiresAt, setAddExpiresAt] = useState<dayjs.Dayjs | null>(null);

  const { data: users = [] } = useQuery<any[]>({
    queryKey: ['rbac-users-all'],
    queryFn: () => $.get('/users?page=1&pageSize=200').then((r: any) => r?.items ?? r ?? []),
    staleTime: 30_000,
  });
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users.slice(0, 30);
    return users.filter((u: any) =>
      String(u.realName ?? u.real_name ?? '').toLowerCase().includes(q)
      || String(u.phone ?? '').includes(q)
      || String(u.id) === q,
    ).slice(0, 50);
  }, [users, search]);

  const { data: effective } = useQuery<Effective>({
    queryKey: ['rbac-effective', selectedUserId],
    queryFn: () => $.get(`/admin/rbac/users/${selectedUserId}/effective-permissions`),
    enabled: selectedUserId != null,
  });

  const { data: perms = [] } = useQuery<Permission[]>({
    queryKey: ['rbac-permissions'],
    queryFn: () => $.get('/admin/rbac/permissions'),
    staleTime: 60_000,
  });

  const addMut = useMutation({
    mutationFn: () => {
      const body: any = { perm_code: addPermCode, reason: addReason || undefined };
      if (addingType === 'grant' && addExpiresAt) body.expires_at = addExpiresAt.toISOString();
      return $.post(`/admin/rbac/users/${selectedUserId}/${addingType}s`, body);
    },
    onSuccess: () => {
      message.success(`已加 ${addingType}`);
      setAddingType(null);
      setAddPermCode(''); setAddReason(''); setAddExpiresAt(null);
      qc.invalidateQueries({ queryKey: ['rbac-effective', selectedUserId] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '失败'),
  });

  const delMut = useMutation({
    mutationFn: ({ type, code }: { type: 'grant' | 'revoke'; code: string }) =>
      $.delete(`/admin/rbac/users/${selectedUserId}/${type}s/${encodeURIComponent(code)}`),
    onSuccess: (_, vars) => {
      message.success(`已删除 ${vars.type}`);
      qc.invalidateQueries({ queryKey: ['rbac-effective', selectedUserId] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message ?? e?.message ?? '失败'),
  });

  // 前端防自锁：admin 给自己 revoke system:rbac_manage
  const isSelfLockAttempt = (type: 'grant' | 'revoke', code: string) =>
    type === 'revoke' && code === 'system:rbac_manage' && Number(selectedUserId) === Number(meUser?.id);

  const onSubmitAdd = () => {
    if (!addPermCode) return message.warning('请选择权限点');
    if (isSelfLockAttempt(addingType!, addPermCode)) {
      return message.error('禁止给自己加 revoke system:rbac_manage（防自锁）');
    }
    addMut.mutate();
  };

  const grantCodes = new Set(effective?.grants.map((g) => g.code) ?? []);
  const revokeCodes = new Set(effective?.revokes.map((r) => r.code) ?? []);
  const rolePerms = new Set(effective?.role_perms ?? []);

  return (
    <div style={{ display: 'flex', gap: 16 }}>
      {/* 左：用户搜索 */}
      <aside style={{ width: 280, flexShrink: 0 }}>
        <Input placeholder="按姓名 / 手机号 / id 搜索"
               value={search} onChange={(e) => setSearch(e.target.value)}
               style={{ marginBottom: 12 }} />
        <div style={{ background: '#FFFFFF', border: '1px solid #DEE0E3', borderRadius: 8, padding: 8, maxHeight: '70vh', overflowY: 'auto' }}>
          {filtered.length === 0 && <div style={{ padding: 20, textAlign: 'center', color: '#8F959E', fontSize: 12 }}>无匹配</div>}
          {filtered.map((u: any) => (
            <div key={u.id}
                 onClick={() => setSelectedUserId(Number(u.id))}
                 style={{
                   padding: '8px 10px', marginBottom: 2, borderRadius: 6, cursor: 'pointer',
                   background: selectedUserId === Number(u.id) ? '#EFF2FE' : 'transparent',
                 }}>
              <div style={{ fontSize: 13, fontWeight: 500 }}>{u.realName ?? u.real_name ?? `#${u.id}`}</div>
              <div style={{ fontSize: 11, color: '#8F959E' }}>{u.role ?? '—'} · {u.phone ?? '—'} · id={u.id}</div>
            </div>
          ))}
        </div>
      </aside>

      {/* 右：effective 权限 */}
      <main style={{ flex: 1, minWidth: 0 }}>
        {selectedUserId == null ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#8F959E' }}>请从左侧选择一个用户</div>
        ) : !effective ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#8F959E' }}>加载中…</div>
        ) : (
          <>
            <div style={{ padding: 16, background: '#FFFFFF', border: '1px solid #DEE0E3', borderRadius: 8, marginBottom: 12 }}>
              <div style={{ fontSize: 16, fontWeight: 500 }}>
                {effective.user.real_name}
                <span style={{ fontSize: 12, color: '#8F959E', marginLeft: 8 }}>
                  {effective.user.role_name ?? effective.user.role} · id={effective.user.id}
                </span>
              </div>
              <div style={{ fontSize: 12, color: '#646A73', marginTop: 6 }}>
                合并后 <strong>{effective.effective.length}</strong> 个 ·
                角色 {effective.role_perms.length} · 加 {effective.grants.length} · 减 {effective.revokes.length}
              </div>
              <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
                <Btn kind="primary" onClick={() => setAddingType('grant')}>+ 加 grant</Btn>
                <Btn onClick={() => setAddingType('revoke')}>+ 加 revoke</Btn>
              </div>
            </div>

            {/* grants */}
            {effective.grants.length > 0 && (
              <Section title={`✚ 用户级加权（${effective.grants.length}）`}>
                {effective.grants.map((g) => (
                  <Chip key={g.code} tone="green"
                        onDelete={() => delMut.mutateAsync({ type: 'grant', code: g.code })}>
                    {g.code}
                    {g.expires_at && <span style={{ marginLeft: 4, fontSize: 10, color: '#646A73' }}>
                      （过期 {fmtDateTime(g.expires_at)}）
                    </span>}
                    {g.reason && <span style={{ marginLeft: 4, fontSize: 10, color: '#8F959E' }}>· {g.reason}</span>}
                  </Chip>
                ))}
              </Section>
            )}

            {/* revokes */}
            {effective.revokes.length > 0 && (
              <Section title={`✖ 用户级减权（${effective.revokes.length}）`}>
                {effective.revokes.map((r) => (
                  <Chip key={r.code} tone="red"
                        onDelete={() => delMut.mutateAsync({ type: 'revoke', code: r.code })}>
                    <span style={{ textDecoration: 'line-through' }}>{r.code}</span>
                    {r.reason && <span style={{ marginLeft: 4, fontSize: 10, color: '#8F959E' }}>· {r.reason}</span>}
                  </Chip>
                ))}
              </Section>
            )}

            {/* role perms (folded) */}
            <Section title={`⚙️ 角色自带（${effective.role_perms.length}）`}>
              {effective.role_perms.map((c) => (
                <Chip key={c} tone="gray">
                  {c}
                  {revokeCodes.has(c) && <span style={{ marginLeft: 4, color: '#CF222E' }}>(已 revoke)</span>}
                </Chip>
              ))}
            </Section>

            {/* 加权 modal */}
            <Modal open={addingType !== null}
                   title={addingType === 'grant' ? '+ 加 grant（用户额外权限）' : '+ 加 revoke（从角色权限中减去）'}
                   onCancel={() => { setAddingType(null); setAddPermCode(''); setAddReason(''); setAddExpiresAt(null); }}
                   onOk={onSubmitAdd} okText="提交" cancelText="取消" confirmLoading={addMut.isPending}>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: '#646A73', marginBottom: 4 }}>权限点</div>
                <Select showSearch placeholder="选权限点 code" style={{ width: '100%' }}
                        value={addPermCode || undefined}
                        onChange={setAddPermCode}
                        filterOption={(input, option) =>
                          String(option?.label ?? '').toLowerCase().includes(input.toLowerCase())}
                        options={perms.map((p) => ({
                          value: p.code,
                          label: `${p.module} · ${p.code} — ${p.name}`,
                        }))} />
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 12, color: '#646A73', marginBottom: 4 }}>原因（可选）</div>
                <Input value={addReason} onChange={(e) => setAddReason(e.target.value)}
                       placeholder="例如：临时处理 X 项目" />
              </div>
              {addingType === 'grant' && (
                <div>
                  <div style={{ fontSize: 12, color: '#646A73', marginBottom: 4 }}>过期时间（可选，留空=永久）</div>
                  <DatePicker showTime value={addExpiresAt} onChange={setAddExpiresAt}
                              style={{ width: '100%' }} format="YYYY-MM-DD HH:mm" />
                </div>
              )}
            </Modal>
          </>
        )}
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: any }) {
  return (
    <div style={{ marginBottom: 12, padding: 16, background: '#FFFFFF', border: '1px solid #DEE0E3', borderRadius: 8 }}>
      <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>{title}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>{children}</div>
    </div>
  );
}

function Chip({ children, tone, onDelete }: { children: any; tone: 'green' | 'red' | 'gray'; onDelete?: () => void | Promise<unknown> }) {
  const bg = tone === 'green' ? '#E8F7EE' : tone === 'red' ? '#FEEBEC' : '#F5F6F7';
  const color = tone === 'green' ? '#1A7F37' : tone === 'red' ? '#CF222E' : '#1F2329';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '3px 8px', borderRadius: 12, background: bg, color, fontSize: 12,
      fontFamily: 'monospace',
    }}>
      {children}
      {onDelete && (
        <Popconfirm title="确认删除？" onConfirm={onDelete} okText="删" cancelText="取消">
          <span style={{ cursor: 'pointer', marginLeft: 4, opacity: 0.7 }}>×</span>
        </Popconfirm>
      )}
    </span>
  );
}

/* ═══════════════════════════════════════════════════════════════
 * ③ 审计日志
 * ═══════════════════════════════════════════════════════════════ */
function AuditLogs() {
  const [actorId, setActorId] = useState('');
  const [targetUserId, setTargetUserId] = useState('');
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 30;

  const params = new URLSearchParams();
  if (actorId) params.set('actor_user_id', actorId);
  if (targetUserId) params.set('target_user_id', targetUserId);
  if (action) params.set('action', action);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));

  const { data, isLoading } = useQuery<{ items: any[]; total: number }>({
    queryKey: ['rbac-audit', actorId, targetUserId, action, page],
    queryFn: () => $.get(`/admin/rbac/audit-logs?${params.toString()}`),
  });

  const actions = ['role_update_perms', 'user_grant', 'user_grant_remove', 'user_revoke', 'user_revoke_remove'];

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, padding: 12, background: '#FFFFFF', border: '1px solid #DEE0E3', borderRadius: 8 }}>
        <Input placeholder="actor user id" value={actorId}
               onChange={(e) => { setActorId(e.target.value); setPage(1); }} style={{ width: 140 }} />
        <Input placeholder="target user id" value={targetUserId}
               onChange={(e) => { setTargetUserId(e.target.value); setPage(1); }} style={{ width: 140 }} />
        <Select placeholder="action" value={action || undefined} allowClear
                onChange={(v) => { setAction(v ?? ''); setPage(1); }} style={{ width: 220 }}
                options={actions.map((a) => ({ value: a, label: a }))} />
        <div style={{ flex: 1 }} />
        <div style={{ alignSelf: 'center', fontSize: 12, color: '#8F959E' }}>共 {data?.total ?? 0} 条</div>
      </div>

      <div style={{ background: '#FFFFFF', border: '1px solid #DEE0E3', borderRadius: 8, overflow: 'hidden' }}>
        <table style={{ width: '100%', fontSize: 13 }}>
          <thead style={{ background: '#F5F6F7' }}>
            <tr style={{ textAlign: 'left' }}>
              <th style={{ padding: 12 }}>时间</th>
              <th style={{ padding: 12 }}>action</th>
              <th style={{ padding: 12 }}>操作人</th>
              <th style={{ padding: 12 }}>对象</th>
              <th style={{ padding: 12 }}>payload</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && <tr><td colSpan={5} style={{ padding: 30, textAlign: 'center', color: '#8F959E' }}>加载中…</td></tr>}
            {(data?.items ?? []).map((it) => (
              <tr key={it.id} style={{ borderTop: '1px solid #F0F2F5' }}>
                <td style={{ padding: 12, whiteSpace: 'nowrap', color: '#646A73' }}>{fmtMonthDayTime(it.created_at)}</td>
                <td style={{ padding: 12, fontFamily: 'monospace', fontSize: 12 }}>{it.action}</td>
                <td style={{ padding: 12 }}>{it.actor_name ?? `#${it.actor_user_id}`}</td>
                <td style={{ padding: 12 }}>
                  {it.target_user_id && <span>👤 {it.target_user_name ?? `#${it.target_user_id}`}</span>}
                  {it.target_role_id && <span>🛡️ 角色 {it.target_role_code ?? `#${it.target_role_id}`}</span>}
                </td>
                <td style={{ padding: 12, fontSize: 11, fontFamily: 'monospace', color: '#646A73', maxWidth: 400, overflowWrap: 'anywhere' }}>
                  {typeof it.payload_json === 'string' ? it.payload_json : JSON.stringify(it.payload_json)}
                </td>
              </tr>
            ))}
            {(data?.items ?? []).length === 0 && !isLoading && (
              <tr><td colSpan={5} style={{ padding: 30, textAlign: 'center', color: '#8F959E' }}>暂无日志</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
        <Btn onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}>上一页</Btn>
        <span style={{ alignSelf: 'center', fontSize: 12, color: '#646A73' }}>第 {page} 页 / 共 {Math.ceil((data?.total ?? 0) / pageSize) || 1}</span>
        <Btn onClick={() => setPage((p) => p + 1)} disabled={(page * pageSize) >= (data?.total ?? 0)}>下一页</Btn>
      </div>
    </div>
  );
}
